"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { SignedIn, SignedOut, SignOutButton, useUser } from "@clerk/nextjs";
import {
  ArrowRight,
  BookA,
  BookOpen,
  Building2,
  ChartLine,
  ChevronDown,
  CircleHelp,
  Code2,
  Compass,
  Gauge,
  Globe,
  ShieldCheck,
  LayoutDashboard,
  LogOut,
  Mail,
  Map as MapIcon,
  MapPin,
  Menu,
  NotebookText,
  Scale,
  Search,
  Sparkles,
  Wifi,
  Wind,
  type LucideIcon,
} from "lucide-react";

import { Link, usePathname, useRouter } from "@/i18n/routing";
import type { PageMetaType } from "@/db/definitions";
import type {
  NavbarCityIndexEntry,
  NavbarLinkData,
} from "@/lib/data/site-links/sections.server";
import type { SiteLink } from "@/lib/data/site-links/types";
import { cn } from "@/lib/utils";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import { useCompareStore } from "@/lib/store/compare-store";
import AnimatedNumber from "@/components/flow/AnimatedNumber";
import LanguageSwitch from "@/components/common/LanguageSwtich";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  dashboardLinks,
  DASHBOARD_HOME_HREF,
} from "@/components/accounts/common/dashboard-links";

const resourceIcons: Partial<Record<PageMetaType, LucideIcon>> = {
  RESOURCE_CATEGORY_LIST: BookOpen,
  RESOURCE_GLOSSARY_HOME: BookA,
  RESOURCE_HANDBOOK_HOME: NotebookText,
  RESOURCE_LOCATION_CITY_LIST: MapPin,
  RESOURCE_MARKET_HOME: ChartLine,
  RESOURCE_PLATFORM_HOME: Globe,
  RESOURCE_ENERGY_LABEL_FINDER: Gauge,
  RESOURCE_INTERNET_HOME: Wifi,
  RESOURCE_INSURANCE_HOME: ShieldCheck,
  RESOURCE_AIR_QUALITY_HOME: Wind,
  OWNER_AGENCY_LIST: Building2,
  FAQ: CircleHelp,
  CONTACT: Mail,
};

// Menu names come from the navbar namespace: the catalog labels are page-meta
// anchors ("Home internet guide", "Données du marché locatif néerlandais")
// and clip in the two-column tiles. The catalog title stays on hover.
const resourceLabelKeys = {
  RESOURCE_CATEGORY_LIST: "categories",
  RESOURCE_GLOSSARY_HOME: "glossary",
  RESOURCE_HANDBOOK_HOME: "handbooks",
  RESOURCE_LOCATION_CITY_LIST: "cities",
  RESOURCE_MARKET_HOME: "market",
  RESOURCE_PLATFORM_HOME: "platforms",
  RESOURCE_ENERGY_LABEL_FINDER: "energy-label",
  RESOURCE_INTERNET_HOME: "internet",
  RESOURCE_INSURANCE_HOME: "insurance",
  RESOURCE_AIR_QUALITY_HOME: "air-quality",
  OWNER_AGENCY_LIST: "agencies",
  FAQ: "faq",
  CONTACT: "contact",
} as const satisfies Partial<Record<PageMetaType, string>>;

const useResourceLabel = () => {
  const t = useTranslations("navbar");
  return (link: SiteLink) =>
    link.pageType in resourceLabelKeys
      ? t(
          `resource-links.${
            resourceLabelKeys[link.pageType as keyof typeof resourceLabelKeys]
          }`,
        )
      : link.label;
};

type Props = {
  locale: string;
  data: NavbarLinkData;
};

const PublicNavbar: React.FC<Props> = ({ locale, data }) => {
  const t = useTranslations("navbar");

  return (
    <header className="google-anno-skip sticky top-0 z-40 w-full border-b border-border bg-background">
      <div className="container flex h-16 items-center gap-2 px-4 lg:px-6">
        <LogoButton />

        <nav
          aria-label={t("menu-title")}
          className="google-anno-skip ml-2 hidden flex-1 lg:block"
        >
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href={data.singles.search?.href ?? "/search"}
                    title={data.singles.search?.title}
                    className={navigationMenuTriggerStyle()}
                  >
                    {t("search")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href={data.singles.map?.href ?? "/map"}
                    title={data.singles.map?.title}
                    className={navigationMenuTriggerStyle()}
                  >
                    {t("map")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger>{t("browse")}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <BrowseMenuContent data={data} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger>{t("resources")}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ResourcesMenuContent data={data} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href={data.singles.pro?.href ?? "/pro"}
                    title={data.singles.pro?.title}
                    className={cn(navigationMenuTriggerStyle(), "gap-1.5")}
                  >
                    <Sparkles
                      className="size-3.5 fill-secondary text-foreground/60"
                      aria-hidden="true"
                    />
                    {t("pro")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href="/publish"
                    className={navigationMenuTriggerStyle()}
                  >
                    {t("publish")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <CompareButton />
          <div className="hidden md:block">
            <LanguageSwitch currentLocale={locale} />
          </div>
          <div className="hidden sm:block">
            <AuthItems />
          </div>
          <MobileMenu locale={locale} data={data} />
        </div>
      </div>
    </header>
  );
};

export default PublicNavbar;

const LogoButton = () => {
  return (
    <Link
      href="/"
      className="flex w-fit shrink-0 flex-row items-center gap-2 rounded-xl px-1.5 py-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Image
        src="/assets/logo.webp"
        alt="Luntero Logo"
        width={100}
        height={100}
        unoptimized
        sizes={IMAGE_SIZES.icon40}
        className="h-auto w-8 rounded-full"
      />
      <span className="text-lg font-bold tracking-tight text-foreground max-[380px]:hidden lg:hidden xl:block">
        LUNTERO
      </span>
    </Link>
  );
};

type MenuContentProps = {
  data: NavbarLinkData;
};

const CanalHousesArt: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 200 58"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <g className="fill-primary">
      {/* step gable */}
      <path d="M10 58 V24 h4 v-6 h4 v-6 h10 v6 h4 v6 h4 v34 Z" />
      {/* spout gable */}
      <path d="M40 58 V20 L52 8 L64 20 V58 Z" />
      {/* bell gable */}
      <path d="M68 58 V22 Q68 12 78 10 Q88 12 88 22 V58 Z" />
      {/* neck gable */}
      <path d="M92 58 V18 h6 v-8 h10 v8 h6 v40 Z" />
      {/* plain pitched */}
      <path d="M120 58 V26 L132 14 L144 26 V58 Z" />
      {/* tall flat with pediment */}
      <path d="M148 58 V16 h-2 l14 -10 14 10 h-2 v42 Z" />
      {/* small step gable */}
      <path d="M180 58 V30 h3 v-5 h3 v-5 h6 v5 h3 v5 h3 v28 Z" />
    </g>
    <g className="fill-secondary">
      <rect x="17" y="28" width="5" height="7" rx="1" />
      <rect x="24" y="28" width="5" height="7" rx="1" />
      <rect x="20" y="42" width="6" height="10" rx="1" />
      <rect x="47" y="24" width="5" height="7" rx="1" />
      <rect x="55" y="24" width="5" height="7" rx="1" />
      <rect x="49" y="40" width="7" height="12" rx="1" />
      <rect x="73" y="24" width="5" height="7" rx="1" />
      <rect x="80" y="24" width="5" height="7" rx="1" />
      <rect x="75" y="40" width="7" height="12" rx="1" />
      <rect x="97" y="22" width="5" height="7" rx="1" />
      <rect x="104" y="22" width="5" height="7" rx="1" />
      <rect x="99" y="38" width="7" height="12" rx="1" />
      <rect x="126" y="30" width="5" height="7" rx="1" />
      <rect x="133" y="30" width="5" height="7" rx="1" />
      <rect x="128" y="44" width="7" height="10" rx="1" />
      <rect x="153" y="22" width="5" height="7" rx="1" />
      <rect x="161" y="22" width="5" height="7" rx="1" />
      <rect x="155" y="38" width="8" height="12" rx="1" />
      <rect x="186" y="35" width="5" height="6" rx="1" />
      <rect x="186" y="46" width="6" height="8" rx="1" />
    </g>
  </svg>
);

/**
 * Rows the desktop grid shows at once. Search results reuse the same count so
 * the popover keeps one height instead of resizing under the cursor. The
 * search itself always runs over every city in the index.
 */
const NAVBAR_CITY_GRID_SLOTS = 16;

const filterCityIndex = (
  index: NavbarCityIndexEntry[],
  query: string,
  limit = NAVBAR_CITY_GRID_SLOTS,
) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return null;
  return index
    .filter((city) => city.name.toLowerCase().includes(normalized))
    .slice(0, limit);
};

const BrowseMenuContent: React.FC<MenuContentProps> = ({ data }) => {
  const t = useTranslations("navbar");
  const [query, setQuery] = useState("");
  const results = filterCityIndex(data.browse.cityIndex, query);

  return (
    <div className="grid w-[660px] grid-cols-[200px_1fr] gap-4 p-4">
      <div className="flex flex-col gap-2">
        {data.browse.home && (
          <NavigationMenuLink asChild>
            <Link
              href={data.browse.home.href}
              title={data.browse.home.title}
              className="flex h-full flex-1 flex-col overflow-hidden rounded-xl bg-secondary p-4 transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <span className="flex size-10 items-center justify-center rounded-xl bg-background">
                <Compass className="size-5" aria-hidden="true" />
              </span>
              <CanalHousesArt className="-mx-4 mt-auto w-[calc(100%+2rem)]" />
              <span className="flex flex-col gap-1 pt-2">
                <span className="text-sm font-semibold text-secondary-foreground">
                  {data.browse.home.label}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-secondary-foreground/70">
                  {t("browse-all")}
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </span>
              </span>
            </Link>
          </NavigationMenuLink>
        )}

        {data.browse.discover && (
          <NavigationMenuLink asChild>
            <Link
              href={data.browse.discover.href}
              title={data.browse.discover.title}
              className="flex flex-none flex-row items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-semibold transition-colors hover:border-zinc-300 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Sparkles
                className="size-3.5 flex-none text-accent"
                aria-hidden="true"
              />
              <span className="line-clamp-1">{t("discover")}</span>
            </Link>
          </NavigationMenuLink>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("city-search-placeholder")}
            aria-label={t("city-search-placeholder")}
            className="h-9 rounded-lg pl-8 text-sm"
          />
        </div>

        {/* Fixed height across all three states, so typing never resizes the
            popover under the cursor. */}
        <div className="flex min-h-[18.375rem] flex-col gap-2">
          <p className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {results ? t("matching-cities") : t("popular-cities")}
          </p>

          {results ? (
            results.length > 0 ? (
              <ul className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                {results.map((city) => (
                  <li key={city.slug}>
                    <NavigationMenuLink asChild>
                      <Link
                        href={`/rent/${city.slug}`}
                        title={t("city-link-title", { city: city.name })}
                        className="flex items-baseline justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      >
                        <span className="truncate text-sm font-medium text-foreground/90">
                          {city.name}
                        </span>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {city.count}
                        </span>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col gap-1 px-2 py-1.5">
                <p className="text-sm text-muted-foreground">
                  {t("city-search-empty")}
                </p>
                <NavigationMenuLink asChild>
                  <Link
                    href={{ pathname: "/search", query: { search: query } }}
                    className="inline-flex w-fit items-center gap-1 text-sm font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    {t("city-search-all", { query: query.trim() })}
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </NavigationMenuLink>
              </div>
            )
          ) : (
            <ul className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {data.browse.cities.map((city) => (
                <li key={city.slug}>
                  <NavigationMenuLink asChild>
                    <Link
                      href={`/rent/${city.slug}`}
                      title={t("city-link-title", { city: city.name })}
                      className="flex items-baseline justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <span className="truncate text-sm font-medium text-foreground/90">
                        {city.name}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {city.count}
                      </span>
                    </Link>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

/** A small bookshelf in the same flat style as the canal houses. */
const ResourceShelfArt: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 200 58"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <g className="fill-primary">
      <rect x="14" y="14" width="14" height="40" rx="2" />
      <rect x="30" y="20" width="12" height="34" rx="2" />
      <rect x="44" y="8" width="16" height="46" rx="2" />
      <path d="M62 54 L74 14 L86 17 L74 54 Z" />
      <rect x="92" y="18" width="14" height="36" rx="2" />
      <rect x="108" y="10" width="12" height="44" rx="2" />
      <rect x="122" y="22" width="16" height="32" rx="2" />
      <rect x="140" y="12" width="12" height="42" rx="2" />
      <rect x="160" y="34" width="28" height="20" rx="3" />
      <rect x="164" y="26" width="20" height="8" rx="2" />
      <rect x="6" y="54" width="188" height="4" rx="2" />
    </g>
    <g className="fill-secondary">
      <rect x="18" y="22" width="6" height="4" rx="1" />
      <rect x="48" y="16" width="8" height="4" rx="1" />
      <rect x="48" y="40" width="8" height="4" rx="1" />
      <rect x="96" y="26" width="6" height="4" rx="1" />
      <rect x="112" y="18" width="4" height="10" rx="1" />
      <rect x="126" y="30" width="8" height="4" rx="1" />
      <rect x="144" y="20" width="4" height="10" rx="1" />
      <circle cx="174" cy="44" r="4" />
    </g>
  </svg>
);

const ResourcesMenuContent: React.FC<MenuContentProps> = ({ data }) => {
  const t = useTranslations("navbar");
  const getLabel = useResourceLabel();

  return (
    <div className="grid w-[880px] grid-cols-[200px_1fr] gap-4 p-4">
      {data.resources.home && (
        <NavigationMenuLink asChild>
          <Link
            href={data.resources.home.href}
            title={data.resources.home.title}
            className="group flex flex-col overflow-hidden rounded-xl bg-secondary p-4 transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="flex size-10 items-center justify-center rounded-xl bg-background">
              <BookOpen className="size-5" aria-hidden="true" />
            </span>
            <span className="flex flex-col gap-1 pt-3">
              <span className="text-sm font-semibold text-secondary-foreground">
                {t("resources-hub.title")}
              </span>
              <span className="text-xs leading-5 text-secondary-foreground/75">
                {t("resources-hub.description")}
              </span>
            </span>
            <ResourceShelfArt className="-mx-4 mt-auto w-[calc(100%+2rem)] pt-4" />
            <span className="inline-flex items-center gap-1 pt-3 text-xs font-semibold text-secondary-foreground">
              {t("all-resources")}
              <ArrowRight
                className="size-3.5 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </span>
          </Link>
        </NavigationMenuLink>
      )}

      <div className="flex min-w-0 flex-col gap-4">
        <div className="grid grid-cols-3 gap-x-4 gap-y-6">
          {data.resources.groups.map((group) => (
            <div key={group.id} className="flex min-w-0 flex-col gap-1.5">
              <p className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(`resource-groups.${group.id}`)}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.links.map((link) => (
                  <li key={link.id}>
                    <ResourceMenuLink link={link} label={getLabel(link)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-auto flex items-center gap-1 border-t border-border pt-3">
          {data.resources.support.length > 0 && (
            <span className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("resource-groups.help")}
            </span>
          )}
          {data.resources.support.map((link) => {
            const Icon = resourceIcons[link.pageType] ?? CircleHelp;
            return (
              <NavigationMenuLink key={link.id} asChild>
                <Link
                  href={link.href}
                  title={link.title}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <Icon
                    className="size-3.5 text-muted-foreground"
                    aria-hidden="true"
                  />
                  {getLabel(link)}
                </Link>
              </NavigationMenuLink>
            );
          })}
          <span className="ml-auto px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("resource-groups.developers")}
          </span>
          <NavigationMenuLink asChild>
            <Link
              href="/developers"
              title={t("api-title")}
              className="group inline-flex min-h-8 items-center gap-1.5 rounded-lg border border-primary bg-secondary px-2.5 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Code2 className="size-3.5" aria-hidden="true" />
              {t("resource-links.api")}
              <ArrowRight
                className="size-3.5 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </NavigationMenuLink>
        </div>
      </div>
    </div>
  );
};

const ResourceMenuLink: React.FC<{ link: SiteLink; label: string }> = ({
  link,
  label,
}) => {
  const Icon = resourceIcons[link.pageType] ?? BookOpen;

  return (
    <NavigationMenuLink asChild>
      <Link
        href={link.href}
        title={link.title}
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary">
          <Icon className="size-3.5" aria-hidden="true" />
        </span>
        <span className="truncate text-sm font-medium text-foreground/90">
          {label}
        </span>
      </Link>
    </NavigationMenuLink>
  );
};

const CompareButton = () => {
  const t = useTranslations("navbar");
  const { listingIds } = useCompareStore();

  return (
    <Button
      asChild
      variant="ghost"
      size="icon-sm"
      className="relative hover:bg-muted"
    >
      <Link href="/compare" title={t("compare")}>
        <Scale className="size-4" aria-hidden="true" />
        <span className="sr-only">{t("compare")}</span>
        {listingIds.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center overflow-hidden rounded-full bg-secondary">
            <AnimatedNumber
              number={listingIds.length}
              className="text-[10px] font-semibold text-secondary-foreground"
            />
          </span>
        )}
      </Link>
    </Button>
  );
};

const AuthItems = () => {
  const t = useTranslations("navbar");

  return (
    <>
      <SignedIn>
        <div className="flex flex-row items-center gap-2">
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="hidden xl:inline-flex"
          >
            <Link href={DASHBOARD_HOME_HREF}>
              <LayoutDashboard className="size-4" aria-hidden="true" />
              <span>{t("dashboard")}</span>
            </Link>
          </Button>
          <AccountMenu />
        </div>
      </SignedIn>
      <SignedOut>
        <Button asChild variant="secondary" size="sm">
          <Link href="/join">
            <span>{t("sign-in")}</span>
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </Button>
      </SignedOut>
    </>
  );
};

const AccountMenu = () => {
  const t = useTranslations("navbar");
  const { user } = useUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("open-account-menu")}
          className="flex items-center gap-1 rounded-full border border-border py-1 pl-1 pr-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <UserAvatar />
          <ChevronDown
            className="size-3.5 text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1.5">
        <DropdownMenuLabel className="flex flex-col gap-0.5 px-2.5">
          <span className="truncate text-sm font-semibold">
            {user?.fullName || user?.firstName || t("default-name")}
          </span>
          {user?.primaryEmailAddress && (
            <span className="truncate text-xs font-normal text-muted-foreground">
              {user.primaryEmailAddress.emailAddress}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {dashboardLinks.map((link) => (
          <DropdownMenuItem key={link.id} asChild>
            <Link
              href={link.href}
              title={t(`dashboard-links.${link.id}.title`)}
              className="cursor-pointer px-2.5 py-2"
            >
              <link.icon className="text-muted-foreground" aria-hidden="true" />
              <span>{t(`dashboard-links.${link.id}.label`)}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <SignOutButton>
          <DropdownMenuItem className="cursor-pointer px-2.5 py-2 text-muted-foreground focus:text-foreground">
            <LogOut aria-hidden="true" />
            <span>{t("log-out")}</span>
          </DropdownMenuItem>
        </SignOutButton>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserAvatar = () => {
  const { user } = useUser();

  if (user?.imageUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={user.imageUrl}
        alt=""
        className="size-7 rounded-full object-cover"
      />
    );
  }

  return (
    <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
      {(user?.firstName || "?").charAt(0).toUpperCase()}
    </span>
  );
};

type MobileMenuProps = {
  locale: string;
  data: NavbarLinkData;
};

const MobileMenu: React.FC<MobileMenuProps> = ({ locale, data }) => {
  const t = useTranslations("navbar");
  const getResourceLabel = useResourceLabel();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <div className="lg:hidden">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="hover:bg-muted"
            aria-label={t("open-menu")}
          >
            <Menu className="size-5" strokeWidth={2.5} />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="flex w-[88%] max-w-sm flex-col gap-0 overflow-y-auto border-none bg-background p-0 scrollbar-none"
        >
          <SheetHeader className="border-b border-border px-4 py-3">
            <LogoButton />
            <SheetTitle className="sr-only">{t("menu-title")}</SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-1 px-3 py-4">
            <MobileSearchInput />

            <div className="pt-2">
              <MobileNavLink
                href={data.singles.search?.href ?? "/search"}
                title={data.singles.search?.title}
                icon={Search}
                label={t("search")}
              />
              <MobileNavLink
                href={data.singles.map?.href ?? "/map"}
                title={data.singles.map?.title}
                icon={MapIcon}
                label={t("map")}
              />

              <MobileSection label={t("browse")} icon={Compass}>
                <MobileBrowseLinks data={data} />
              </MobileSection>

              <MobileSection label={t("resources")} icon={BookOpen}>
                {data.resources.home && (
                  <MobileSubLink
                    href={data.resources.home.href}
                    title={data.resources.home.title}
                    label={t("all-resources")}
                    strong
                  />
                )}
                {[
                  ...data.resources.groups,
                  { id: "help" as const, links: data.resources.support },
                ].map(
                  (group) =>
                    group.links.length > 0 && (
                      <div key={group.id} className="flex flex-col pt-2">
                        <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {t(`resource-groups.${group.id}`)}
                        </p>
                        {group.links.map((link) => (
                          <MobileSubLink
                            key={link.id}
                            href={link.href}
                            title={link.title}
                            label={getResourceLabel(link)}
                            icon={resourceIcons[link.pageType] ?? BookOpen}
                          />
                        ))}
                      </div>
                    ),
                )}
                <div className="flex flex-col pt-2">
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("resource-groups.developers")}
                  </p>
                  <MobileSubLink
                    href="/developers"
                    title={t("api-title")}
                    label={t("resource-links.api")}
                    icon={Code2}
                  />
                </div>
              </MobileSection>

              <MobileNavLink
                href="/publish"
                icon={Building2}
                label={t("publish")}
              />
              <MobileNavLink
                href={data.singles.pro?.href ?? "/pro"}
                title={data.singles.pro?.title}
                icon={Sparkles}
                label={t("pro")}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-border px-4 py-4">
            <MobileAuthItems />
            <LanguageSwitch currentLocale={locale} version="full" />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const MobileSearchInput = () => {
  const t = useTranslations("navbar");
  const router = useRouter();
  const [value, setValue] = useState("");

  const onSearch = () => {
    router.push({
      pathname: "/search",
      query: {
        search: value,
      },
    });
  };

  return (
    <div className="flex flex-row items-center gap-2">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("input-placeholder")}
        className="w-full text-sm"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <Button
        aria-label={t("input-placeholder")}
        size="icon"
        variant="secondary"
        onClick={onSearch}
        type="button"
      >
        <Search className="size-5" />
      </Button>
    </div>
  );
};

type MobileNavLinkProps = {
  href: string;
  title?: string;
  icon: LucideIcon;
  label: string;
};

const MobileNavLink: React.FC<MobileNavLinkProps> = ({
  href,
  title,
  icon: Icon,
  label,
}) => {
  return (
    <Link
      href={href}
      title={title}
      className="flex min-h-11 items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="text-sm font-semibold">{label}</span>
    </Link>
  );
};

type MobileSectionProps = {
  label: string;
  icon: LucideIcon;
  children: React.ReactNode;
};

const MobileSection: React.FC<MobileSectionProps> = ({
  label,
  icon: Icon,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
            <Icon className="size-4" aria-hidden="true" />
          </span>
          <span className="text-sm font-semibold">{label}</span>
        </span>
        <ChevronDown
          className={cn(
            "size-4 text-muted-foreground transition-transform",
            isExpanded && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      {isExpanded && (
        <div className="ml-[22px] flex flex-col border-l border-border pl-4 pt-1">
          {children}
        </div>
      )}
    </div>
  );
};

const MobileBrowseLinks: React.FC<MenuContentProps> = ({ data }) => {
  const t = useTranslations("navbar");
  const [query, setQuery] = useState("");
  const results = filterCityIndex(data.browse.cityIndex, query, 10);

  return (
    <>
      {data.browse.home && (
        <MobileSubLink
          href={data.browse.home.href}
          title={data.browse.home.title}
          label={data.browse.home.label}
          strong
        />
      )}
      {data.browse.discover && (
        <MobileSubLink
          href={data.browse.discover.href}
          title={data.browse.discover.title}
          label={t("discover")}
        />
      )}
      <div className="relative my-1 pr-2">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("city-search-placeholder")}
          aria-label={t("city-search-placeholder")}
          className="h-9 rounded-lg pl-8 text-sm"
        />
      </div>
      {results ? (
        results.length > 0 ? (
          results.map((city) => (
            <MobileSubLink
              key={city.slug}
              href={`/rent/${city.slug}`}
              title={t("city-link-title", { city: city.name })}
              label={city.name}
            />
          ))
        ) : (
          <div className="flex flex-col gap-1 px-2 py-2">
            <p className="text-sm text-muted-foreground">
              {t("city-search-empty")}
            </p>
            <Link
              href={{ pathname: "/search", query: { search: query } }}
              className="inline-flex w-fit items-center gap-1 text-sm font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t("city-search-all", { query: query.trim() })}
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        )
      ) : (
        data.browse.cities.map((city) => (
          <MobileSubLink
            key={city.slug}
            href={`/rent/${city.slug}`}
            title={t("city-link-title", { city: city.name })}
            label={city.name}
          />
        ))
      )}
    </>
  );
};

type MobileSubLinkProps = {
  href: string;
  title?: string;
  label: string;
  strong?: boolean;
  icon?: LucideIcon;
};

const MobileSubLink: React.FC<MobileSubLinkProps> = ({
  href,
  title,
  label,
  strong,
  icon: Icon,
}) => {
  return (
    <Link
      href={href}
      title={title}
      className={cn(
        "flex min-h-10 items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        strong && "font-semibold text-foreground",
      )}
    >
      {Icon && (
        <Icon
          className="size-4 flex-none text-muted-foreground"
          aria-hidden="true"
        />
      )}
      <span className="min-w-0 truncate">{label}</span>
    </Link>
  );
};

const MobileAuthItems = () => {
  const t = useTranslations("navbar");

  return (
    <>
      <SignedIn>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 px-1 pb-2">
            <UserAvatar />
            <MobileUserName />
          </div>
          {dashboardLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              title={t(`dashboard-links.${link.id}.title`)}
              className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <link.icon
                className="size-4 text-muted-foreground"
                aria-hidden="true"
              />
              {t(`dashboard-links.${link.id}.label`)}
            </Link>
          ))}
          <SignOutButton>
            <button
              type="button"
              className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <LogOut className="size-4" aria-hidden="true" />
              {t("log-out")}
            </button>
          </SignOutButton>
        </div>
      </SignedIn>
      <SignedOut>
        <Button asChild variant="secondary" className="w-full">
          <Link href="/join">
            <span>{t("sign-in")}</span>
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </Button>
      </SignedOut>
    </>
  );
};

const MobileUserName = () => {
  const t = useTranslations("navbar");
  const { user } = useUser();

  return (
    <span className="truncate text-sm font-semibold">
      {user?.fullName || user?.firstName || t("default-name")}
    </span>
  );
};
